import type { LegislationItem } from "@shared/schema";

export const legislationData: LegislationItem[] = [
  {
    id: "ria-2022",
    title: "EB-5 Reform and Integrity Act of 2022",
    status: "active",
    effectiveDate: "March 15, 2022",
    expirationDate: "September 30, 2027",
    summary: "Comprehensive reform of the EB-5 program reauthorizing Regional Centers, creating new set-aside visa categories, and implementing enhanced integrity measures.",
    impact: "This legislation reauthorized the Regional Center program after it had lapsed, created Rural, High Unemployment, and Infrastructure set-aside categories with reserved visas, increased minimum investment amounts to $800,000 (TEA) and $1,050,000 (non-TEA), and added new compliance requirements for Regional Centers.",
    grandfatheringInfo: "Investors who filed I-526 petitions before March 15, 2022 may be grandfathered under the previous investment amounts ($500,000 TEA / $1,000,000 non-TEA). Additionally, certain Regional Center designations and material change requirements have grandfathering provisions for pre-RIA projects.",
  },
  {
    id: "rc-sunset",
    title: "Regional Center Program Sunset",
    status: "active",
    effectiveDate: "March 15, 2022",
    expirationDate: "September 30, 2027",
    summary: "The Regional Center program was reauthorized through September 30, 2027. Congress must act to extend it before this date.",
    impact: "If Congress does not reauthorize the program before September 30, 2027, the Regional Center program will lapse again. During a lapse, no new Regional Center petitions can be filed, and pending petitions cannot be adjudicated. Direct EB-5 remains unaffected by program lapses.",
    grandfatheringInfo: "Investors with approved I-526/I-526E petitions may be protected during a lapse, though this depends on their specific case status and USCIS policy at the time.",
  },
  {
    id: "investment-increase-2024",
    title: "Automatic Investment Amount Adjustments",
    status: "active",
    effectiveDate: "January 1, 2027",
    summary: "The EB-5 Reform and Integrity Act includes provisions for automatic adjustment of investment amounts based on inflation every 5 years.",
    impact: "Starting January 1, 2027, the minimum investment amounts may be adjusted for inflation. The TEA amount could increase from $800,000 and the non-TEA from $1,050,000. Investors should be aware of potential timing implications.",
    grandfatheringInfo: "Filing your I-526E before any investment increase locks in the current amount. Once your petition is filed, subsequent increases do not affect your case.",
  },
  {
    id: "set-aside-creation",
    title: "Set-Aside Visa Categories",
    status: "active",
    effectiveDate: "March 15, 2022",
    summary: "Creation of reserved EB-5 visa allocations for Rural (20%), High Unemployment (10%), and Infrastructure (2%) projects.",
    impact: "These set-aside categories have their own visa allocation separate from the unreserved category. Currently, all set-aside categories are 'Current' for ALL countries including China and India. This represents a major opportunity for investors from countries with long unreserved backlogs.",
    grandfatheringInfo: "Investors cannot retroactively move to set-aside categories. You must file a new petition for a qualifying set-aside project to benefit from these reserved visas.",
  },
  {
    id: "concurrent-filing",
    title: "Concurrent Filing Authorization",
    status: "active",
    effectiveDate: "March 15, 2022",
    summary: "The 2022 Act explicitly authorized concurrent filing of I-526E and I-485 when a visa is immediately available.",
    impact: "Investors in the US with current priority dates can file their adjustment of status simultaneously with their I-526E petition. This allows faster access to work permits, travel documents, and starts the clock for naturalization earlier.",
    grandfatheringInfo: "N/A - This is an ongoing benefit available to qualifying current filers.",
  },
  {
    id: "integrity-fund",
    title: "EB-5 Integrity Fund",
    status: "active",
    effectiveDate: "March 15, 2022",
    summary: "Establishment of an Integrity Fund financed by fees from EB-5 participants to fund enhanced oversight and compliance.",
    impact: "Regional Centers, investors, and project operators pay fees that support USCIS's enhanced oversight capabilities. Annual fees for Regional Centers range from $10,000 to $20,000 depending on investor count. This has improved program integrity but added costs.",
    grandfatheringInfo: "N/A - All participants in the program are subject to Integrity Fund fees.",
  },
];

export interface LawProvision {
  id: string;
  title: string;
  icon: string;
  before: string;
  after: string;
  whatItMeans: string;
  whoBenefits: string[];
  practicalTip: string;
  section: string;
}

export const lawProvisions: LawProvision[] = [
  {
    id: "investment-amounts",
    title: "Investment Amounts Increased",
    icon: "dollar",
    section: "Section 203(b)(5)(C)",
    before: "$500,000 for TEA projects, $1,000,000 for non-TEA projects",
    after: "$800,000 for TEA projects, $1,050,000 for non-TEA projects (with inflation adjustments every 5 years)",
    whatItMeans: "The minimum investment required to qualify for an EB-5 visa increased significantly. However, this increase was paired with the creation of set-aside categories that offer faster processing for qualifying projects. The law also locked in these amounts until 2027, giving investors certainty about costs.",
    whoBenefits: [
      "Investors who filed before March 15, 2022 (grandfathered at old amounts)",
      "Investors who can access TEA projects vs non-TEA",
      "Investors who act before the 2027 inflation adjustment"
    ],
    practicalTip: "If you're on the fence, filing before January 1, 2027 locks in current amounts. The next adjustment could add $50,000-$100,000 based on inflation."
  },
  {
    id: "set-aside-categories",
    title: "Reserved Visa Categories Created",
    icon: "layers",
    section: "Section 203(b)(5)(B)(ii)",
    before: "All EB-5 visas came from a single pool of ~10,000 per year, subject to per-country limits",
    after: "32% of visas are now reserved: Rural (20%), High Unemployment TEA (10%), Infrastructure (2%)",
    whatItMeans: "This is one of the most significant changes for investors from high-demand countries. Previously, investors from China and India faced extended waits due to per-country limits on the unreserved category. Now, the set-aside categories have their own visa pools. Check the current visa bulletin for the latest priority date status - as of early 2026, set-aside categories have generally shown better availability than unreserved for backlogged countries.",
    whoBenefits: [
      "Investors from countries with unreserved backlogs (such as China and India)",
      "Any investor wanting potentially faster visa availability",
      "Rural communities receiving more EB-5 investment"
    ],
    practicalTip: "If you're from a country with unreserved backlogs, explore Rural or High Unemployment TEA projects. Check the visa bulletin regularly to see current wait times for each category."
  },
  {
    id: "regional-center-reauthorization",
    title: "Regional Center Program Restored",
    icon: "building",
    section: "Section 203(b)(5)(E)",
    before: "Program lapsed on June 30, 2021 after Congress failed to extend",
    after: "Reauthorized through September 30, 2027 with enhanced oversight requirements",
    whatItMeans: "After a lapse period where no Regional Center petitions could be processed, Congress reauthorized the program. The vast majority of EB-5 investors historically have used Regional Centers because of the indirect job counting benefit. The reauthorization came with stricter compliance requirements for Regional Centers.",
    whoBenefits: [
      "Regional Center investors whose petitions were paused during the lapse",
      "Future investors who want to use indirect job counting",
      "Regional Center operators who can now accept new investors"
    ],
    practicalTip: "Watch for the 2027 sunset. Start your EB-5 process at least 2 years before to ensure your petition is filed before any potential lapse."
  },
  {
    id: "concurrent-filing",
    title: "Concurrent Filing Allowed",
    icon: "files",
    section: "Section 204(a)(1)(H)",
    before: "Had to wait for I-526 approval before filing I-485 adjustment of status",
    after: "Can file I-526E and I-485 at the same time if a visa is immediately available",
    whatItMeans: "This is huge for investors already in the US. Instead of waiting 2+ years for I-526E approval before getting work authorization, you can now file your green card application immediately if you qualify for a set-aside category. This means work permits (EAD) and travel documents (Advance Parole) within months, not years.",
    whoBenefits: [
      "Investors already in the US on other visa types (H-1B, F-1, L-1, etc.)",
      "Investors in set-aside categories with immediate visa availability",
      "Families who need work authorization for spouses"
    ],
    practicalTip: "If you're in the US and invest in a Rural or TEA project, you may be able to get work authorization within 4-6 months through concurrent filing, even while your I-526E is pending."
  },
  {
    id: "integrity-measures",
    title: "Enhanced Fraud Prevention",
    icon: "shield",
    section: "Multiple Sections",
    before: "Limited USCIS oversight, multiple fraud cases damaged program reputation",
    after: "Mandatory audits, background checks for all principals, Integrity Fund fees, source of funds scrutiny",
    whatItMeans: "The law added multiple layers of protection for investors. Regional Centers must undergo audits at least every 5 years. All people involved in Regional Centers must pass background checks. An Integrity Fund (paid by fees) gives USCIS resources to investigate fraud. While this adds costs and processing time, it makes your investment significantly safer.",
    whoBenefits: [
      "All investors (better protection against fraud)",
      "Legitimate Regional Centers (distinguishes them from bad actors)",
      "The EB-5 program's long-term reputation"
    ],
    practicalTip: "Ask your Regional Center about their compliance procedures and when their last USCIS audit was. A legitimate center will be transparent about this."
  },
  {
    id: "job-creation-rules",
    title: "Job Creation Requirements Tightened",
    icon: "users",
    section: "Section 203(b)(5)(E)(iv)",
    before: "Up to 100% of jobs could be indirect (estimated through economic models)",
    after: "Maximum 90% indirect jobs (75% if construction is less than 2 years)",
    whatItMeans: "Previously, all 10 required jobs could be 'indirect' - jobs estimated through economic formulas rather than actual employees. Now, at least 10% must be direct employees. For short construction projects, 25% must be direct. This makes job creation claims more concrete and verifiable.",
    whoBenefits: [
      "Investors (more reliable job creation)",
      "Projects with strong direct employment",
      "Long-term operational projects vs quick construction flips"
    ],
    practicalTip: "Look for projects with operating businesses that will have real employees, not just construction-only projects that flip immediately after completion."
  },
  {
    id: "priority-date-retention",
    title: "Priority Date Portability",
    icon: "calendar",
    section: "Section 203(b)(5)(K)",
    before: "If your project failed, you lost your place in line",
    after: "You can keep your original priority date if you reinvest within a reasonable time",
    whatItMeans: "This is a safety net. If your original EB-5 project fails or your petition is denied through no fault of your own, you don't have to go to the back of the visa line. You can reinvest in a new project and keep your original priority date. This is especially valuable for Chinese investors who have waited years.",
    whoBenefits: [
      "Investors whose projects fail",
      "Investors with denied petitions due to project issues (not fraud)",
      "Long-backlog country investors who can't afford to lose their place"
    ],
    practicalTip: "This only works if the failure wasn't your fault and you didn't participate in fraud. Always keep detailed records of your investment timeline."
  },
  {
    id: "source-of-funds",
    title: "Source of Funds Clarity",
    icon: "search",
    section: "Section 203(b)(5)(F)",
    before: "Vague requirements, inconsistent adjudication",
    after: "Clearer standards for lawful source documentation, but stricter enforcement",
    whatItMeans: "You must prove your investment money came from legitimate sources - this was always true, but now it's more formally defined. USCIS wants a clear paper trail: earnings, property sales, inheritance, gifts, etc. They're specifically looking for clean money that isn't connected to any illegal activity.",
    whoBenefits: [
      "Investors with clear, documented wealth",
      "Investors who use professional immigration attorneys for document preparation",
      "The program's legitimacy overall"
    ],
    practicalTip: "Start gathering financial documentation early - ideally 6+ months before you plan to file. Tax returns, property records, business ownership documents, and bank statements for the past 5-7 years may all be needed."
  }
];

export interface LawTimeline {
  date: string;
  event: string;
  type: "positive" | "neutral" | "warning";
}

export const lawTimeline: LawTimeline[] = [
  { date: "April 28, 2021", event: "EB-5 Reform and Integrity Act introduced in House (H.R. 2901)", type: "neutral" },
  { date: "June 30, 2021", event: "Regional Center program lapsed - no new Regional Center petitions could be filed during lapse", type: "warning" },
  { date: "March 10, 2022", event: "Senate passed EB-5 reform as part of omnibus spending bill (H.R. 2471)", type: "positive" },
  { date: "March 15, 2022", event: "President signed bill into law - EB-5 Reform and Integrity Act takes effect", type: "positive" },
  { date: "Spring 2022", event: "USCIS resumed processing Regional Center petitions that were paused during lapse", type: "positive" },
  { date: "2023 onwards", event: "USCIS began adjudicating new I-526E petitions filed under the reformed program", type: "positive" },
  { date: "January 1, 2027", event: "Scheduled date for potential investment amount adjustment based on inflation (per statute)", type: "warning" },
  { date: "September 30, 2027", event: "Regional Center program authorization expires unless Congress acts to extend", type: "warning" },
];

export const quickFacts = [
  { label: "Law Name", value: "EB-5 Reform and Integrity Act of 2022" },
  { label: "Bill Number", value: "H.R. 2901 (incorporated into H.R. 2471)" },
  { label: "Signed Into Law", value: "March 15, 2022" },
  { label: "Sponsor", value: "Rep. Greg Stanton (D-AZ)" },
  { label: "Expires", value: "September 30, 2027" },
  { label: "TEA Investment", value: "$800,000 minimum" },
  { label: "Non-TEA Investment", value: "$1,050,000 minimum" },
];

export const getActiveLeglislation = () =>
  legislationData.filter((leg) => leg.status === "active");

export const getGrandfatheringInfo = () =>
  legislationData.filter((leg) => leg.grandfatheringInfo);

export function calculateGrandfatheringDeadlines(): {
  deadline: string;
  description: string;
  urgency: "critical" | "important" | "informational";
}[] {
  return [
    {
      deadline: "September 30, 2027",
      description: "Regional Center program sunset date. File before this date to ensure your petition can be adjudicated under current law.",
      urgency: "important" as const,
    },
    {
      deadline: "January 1, 2027",
      description: "Potential investment amount increase due to inflation adjustment. Filing before this date locks in current $800,000/$1,050,000 amounts.",
      urgency: "important" as const,
    },
    {
      deadline: "Ongoing",
      description: "Set-aside categories (Rural, High Unemployment, Infrastructure) currently have no backlog. This could change if demand increases significantly.",
      urgency: "informational" as const,
    },
  ];
}
